import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Ticket,
  Boxes,
  Users,
  Package,
  UserCog,
  Settings,
  ShieldCheck,
  ScrollText,
  HeartPulse,
  Tags,
  BarChart3,
  Gauge,
  CalendarDays,
  Building2,
  LogOut,
  Menu,
  X,
  Bell,
  Moon,
  Sun,
  ChevronDown,
  UserCircle,
  Activity,
  BookOpen,
  PenLine,
  Download,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import BenDeskLogo from "./BenDeskLogo";
import Avatar from "./ui/Avatar";
import api, { API_BASE_URL } from "../services/api";
import notificationsService from "../services/notificationsService";
import { getSoundPref, playNewTicketSound, unlockAudioOnce } from "../utils/notificationSound";
import { getTheme, toggleTheme } from "../utils/theme";
import { timeAgo } from "../utils/datetime";

const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, "");

const routeTitles = {
  "/dashboard": "Dashboard",
  "/tickets": "Chamados",
  "/assets": "Ativos",
  "/collaborators/departments": "Departamentos",
  "/collaborators/fluxograma": "Fluxograma de Colaboradores",
  "/collaborators": "Colaboradores",
  "/stock": "Estoque",
  "/base-conhecimento": "Base de Conhecimento",
  "/assinaturas": "Assinaturas",
  "/users": "Usuários",
  "/settings": "Configurações",
  "/permissoes": "Permissões",
  "/auditoria": "Auditoria",
  "/saude": "Saúde da Plataforma",
  "/categorias": "Categorias de Chamado",
  "/relatorios": "Relatórios",
  "/sla": "SLA",
  "/calendario": "Calendário",
  "/tenants": "Empresas",
  "/perfil": "Meu perfil",
  "/minha-atividade": "Minha atividade",
  "/instalar": "Instalar",
};

// Ambiente + commit. Fallback do build (REACT_APP_*); em runtime buscamos /api/version
// (o backend lê o SOURCE_COMMIT que o Coolify injeta no runtime → commit real).
const APP_ENV = process.env.REACT_APP_ENV || "production";
const APP_COMMIT = process.env.REACT_APP_COMMIT || "";
const shortCommit = (c) => (/^[0-9a-f]{7,40}$/i.test(c || "") ? String(c).slice(0, 7) : "");
const envLabelOf = (e) =>
  e === "production" ? "Produção" : e === "homologation" ? "Homologação" : "Desenvolvimento";


const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { can } = usePermissions();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'user' | 'bell' | null
  const [sysInfo, setSysInfo] = useState(null);
  const [theme, setTheme] = useState(getTheme());
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  }, [location.pathname]);

  const activeTenantName = useMemo(() => {
    return localStorage.getItem("active_tenant_name") || "";
  }, [location.pathname]);

  const isAdmin =
    user?.profile?.toLowerCase() === "administrador" ||
    user?.profile?.toLowerCase() === "admin";
  const isPlatformAdmin = !!user?.is_platform_admin;

  const sections = [
    {
      label: "Visão Geral",
      items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
    },
    {
      label: "Operação",
      items: [
        { to: "/tickets", label: "Chamados", icon: Ticket },
        { to: "/assets", label: "Ativos", icon: Boxes },
        { to: "/collaborators", label: "Colaboradores", icon: Users },
        { to: "/stock", label: "Estoque", icon: Package },
        { to: "/base-conhecimento", label: "Base de Conhecimento", icon: BookOpen },
        { to: "/assinaturas", label: "Assinaturas", icon: PenLine },
      ],
    },
    {
      label: "Administração",
      items: [
        ...(isAdmin ? [{ to: "/users", label: "Usuários", icon: UserCog }] : []),
        ...(isPlatformAdmin ? [{ to: "/tenants", label: "Empresas", icon: Building2 }] : []),
        ...(isAdmin ? [{ to: "/permissoes", label: "Permissões", icon: ShieldCheck }] : []),
        ...(isAdmin ? [{ to: "/relatorios", label: "Relatórios", icon: BarChart3 }] : []),
        ...(isAdmin ? [{ to: "/saude", label: "Saúde da Plataforma", icon: HeartPulse }] : []),
        ...(isAdmin ? [{ to: "/auditoria", label: "Auditoria", icon: ScrollText }] : []),
        ...(isAdmin ? [{ to: "/sla", label: "SLA", icon: Gauge }] : []),
        ...(isAdmin ? [{ to: "/calendario", label: "Calendário", icon: CalendarDays }] : []),
        ...(isAdmin ? [{ to: "/categorias", label: "Categorias de Chamado", icon: Tags }] : []),
        ...(isAdmin ? [{ to: "/settings", label: "Configurações", icon: Settings }] : []),
      ],
    },
    {
      label: "Conta",
      items: [
        { to: "/minha-atividade", label: "Minha atividade", icon: Activity },
        { to: "/perfil", label: "Meu perfil", icon: UserCircle },
        { to: "/instalar", label: "Instalar", icon: Download },
      ],
    },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const fullName =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Usuário";
  const profile = user?.profile || "Sem perfil";
  const email = user?.email || "";
  const company = activeTenantName || "";
  const avatarUrl = user?.avatar_url ? `${API_ORIGIN}${user.avatar_url}` : null;

  useEffect(() => {
    setIsSidebarOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  // Fecha os dropdowns da topbar ao clicar em qualquer lugar (adiciona o
  // listener no próximo tick pra não fechar no mesmo clique que abriu).
  useEffect(() => {
    if (!openMenu) return undefined;
    const close = () => setOpenMenu(null);
    const id = setTimeout(() => window.addEventListener("click", close), 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("click", close);
    };
  }, [openMenu]);

  // Ambiente + commit real (backend lê o SOURCE_COMMIT do Coolify em runtime).
  useEffect(() => {
    let alive = true;
    api.get("/version").then((r) => { if (alive) setSysInfo(r.data); }).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Contagem de notificações não lidas: no boot + polling leve a cada 60s.
  useEffect(() => {
    let alive = true;
    const tick = () =>
      notificationsService
        .unreadCount()
        .then((r) => { if (alive) setUnreadCount(r?.count || 0); })
        .catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Som de "novo chamado": detecta quando entra um chamado novo na empresa
  // (maior ID aumentou) e toca o som que o usuário escolheu no Meu Perfil.
  // Só consulta quando o som está ligado (padrão é desligado, sem custo).
  const lastTicketIdRef = useRef(null);
  useEffect(() => {
    unlockAudioOnce(); // destrava o áudio no 1º gesto (política de autoplay)
    let alive = true;
    const tick = async () => {
      if (getSoundPref() === "off") return;
      try {
        const r = await api.get("/tickets/latest-id");
        const id = r?.data?.latest_id || 0;
        if (!alive) return;
        if (lastTicketIdRef.current == null) {
          lastTicketIdRef.current = id; // baseline: não toca pros já existentes
          return;
        }
        if (id > lastTicketIdRef.current) {
          lastTicketIdRef.current = id;
          playNewTicketSound();
        }
      } catch {
        /* silencioso */
      }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Carrega a lista ao abrir o sininho.
  const openBell = () => {
    const next = openMenu === "bell" ? null : "bell";
    setOpenMenu(next);
    if (next === "bell") {
      setNotifLoading(true);
      notificationsService
        .list({ limit: 15 })
        .then((rows) => setNotifications(Array.isArray(rows) ? rows : []))
        .catch(() => setNotifications([]))
        .finally(() => setNotifLoading(false));
    }
  };

  const handleNotifClick = (n) => {
    setOpenMenu(null);
    if (!n.is_read) {
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((list) =>
        list.map((x) => (x.id === n.id ? { ...x, is_read: true } : x))
      );
      notificationsService.markRead(n.id).catch(() => {});
    }
    if (n.ticket_id) navigate(`/tickets/${n.ticket_id}`);
  };

  const handleMarkAll = () => {
    setUnreadCount(0);
    setNotifications((list) => list.map((x) => ({ ...x, is_read: true })));
    notificationsService.markAllRead().catch(() => {});
  };

  const headerTitle =
    Object.entries(routeTitles).find(([route]) =>
      location.pathname === route || location.pathname.startsWith(`${route}/`)
    )?.[1] || "BenDesk";

  const envLabel = envLabelOf(sysInfo?.environment || APP_ENV);
  const commitShort = shortCommit(sysInfo?.commit || APP_COMMIT);

  const sidebarBody = (
    <>
      <div className="h-16 flex items-center justify-between px-5 border-b border-sidebar-border shrink-0">
        <BenDeskLogo
          markSize={26}
          showTagline={false}
          markClassName="text-white"
          badgeClassName="bg-gradient-to-br from-blue-500 to-blue-700 shadow-md"
          wordClassName="text-white"
          deskClassName="text-blue-400"
        />
        <button
          type="button"
          className="lg:hidden text-sidebar-foreground/70 hover:text-white"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Fechar menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-5 space-y-6">
        {sections.map((section) => {
          // Esconde itens de tela que o perfil nao pode ver (admin faz bypass).
          const visibleItems = section.items.filter((item) => can("view", item.to));
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label}>
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/50">
                {section.label}
              </p>
              <ul className="space-y-1">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                            isActive
                              ? "bg-sidebar-accent text-white shadow-sm border-l-2 border-sidebar-primary"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-white"
                          }`
                        }
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-4 shrink-0">
        <p className="text-xs font-semibold text-white">Ambiente {envLabel}</p>
        <p className="text-[11px] text-sidebar-foreground/60 mt-1 font-mono" title={sysInfo?.commit || APP_COMMIT}>
          {commitShort ? `BenDesk · main@${commitShort}` : "BenDesk ITSM · v1.0"}
        </p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background lg:flex">
      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-40 bg-slate-950/60 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`no-print fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-gradient-sidebar text-sidebar-foreground border-r border-sidebar-border transform transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:self-start lg:w-64 lg:max-w-none lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarBody}
      </aside>

      <div className="flex-1 flex flex-col min-h-screen lg:min-w-0">
        <header className="no-print h-16 sticky top-0 z-30 bg-card/80 backdrop-blur border-b border-border flex items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center rounded-lg border border-border p-2 text-foreground"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu size={20} />
            </button>
            <div className="text-xl sm:text-2xl font-bold text-foreground truncate">
              {headerTitle}
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Tema (em breve) */}
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              aria-label="Alternar tema"
              onClick={() => setTheme(toggleTheme())}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Notificações */}
            <div className="relative">
              <button
                type="button"
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                onClick={openBell}
                aria-label={`Notificações${unreadCount ? ` (${unreadCount} não lidas)` : ""}`}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {openMenu === "bell" && (
                <div
                  className="absolute right-0 mt-2 w-80 max-w-[90vw] rounded-xl border border-border bg-popover shadow-lg-soft z-50 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground">Notificações</p>
                    {notifications.some((n) => !n.is_read) && (
                      <button
                        type="button"
                        onClick={handleMarkAll}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Marcar todas como lidas
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto scrollbar-thin">
                    {notifLoading ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                        Carregando...
                      </p>
                    ) : notifications.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                        Você não tem notificações.
                      </p>
                    ) : (
                      notifications.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotifClick(n)}
                          className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${
                            n.is_read ? "" : "bg-primary/5"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.is_read && (
                              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                            )}
                            <div className={`min-w-0 flex-1 ${n.is_read ? "pl-4" : ""}`}>
                              <p className="text-sm font-medium text-foreground truncate">
                                {n.title}
                              </p>
                              {n.body && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {n.body}
                                </p>
                              )}
                              <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                                {timeAgo(n.created_at)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Usuário */}
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-2.5 rounded-lg pl-1.5 pr-2 py-1 hover:bg-muted transition-colors"
                onClick={() => setOpenMenu(openMenu === "user" ? null : "user")}
              >
                <Avatar src={avatarUrl} name={fullName} className="h-9 w-9 ring-2 ring-primary/15" />
                <span className="hidden md:block text-left leading-tight">
                  <span className="block text-sm font-semibold text-foreground">{fullName}</span>
                  {company && <span className="block text-xs text-muted-foreground">{company}</span>}
                </span>
                <ChevronDown size={16} className="text-muted-foreground hidden md:block" />
              </button>

              {openMenu === "user" && (
                <div className="absolute right-0 mt-2 w-60 rounded-xl border border-border bg-popover shadow-lg-soft z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-sm font-semibold text-foreground truncate">{fullName}</p>
                    <p className="text-xs text-muted-foreground truncate">{email}</p>
                    <p className="text-xs text-primary mt-0.5 truncate">{company ? `${company} · ` : ""}{profile}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/perfil")}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted text-left"
                  >
                    <UserCircle size={16} /> Meu perfil
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/minha-atividade")}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted text-left"
                  >
                    <Activity size={16} /> Minha atividade
                  </button>
                  <div className="border-t border-border" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 text-left"
                  >
                    <LogOut size={16} /> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
